import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { ArrowRight, CheckCircle, Phone } from 'lucide-react'
import { SEO } from '@/lib/seo'
import { Button, Badge, AnimatedCard, CardHeader, CardTitle, CardDescription } from '@/components/ui'
import { CTASection } from '@/components/sections'
import { assessments } from '@/data/assessments'
import { companyInfo } from '@/data/company'

export function AssessmentsPage() {
  return (
    <>
      <SEO
        title="Free Technology Assessments"
        description="UC Advisors offers complimentary assessments to help optimize your technology strategy. Cybersecurity, Cloud, Network, Data Analytics, AI, UCaaS, and Contact Center assessments."
      />

      {/* Hero Section */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0 bg-hero-pattern opacity-50" />
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <Badge variant="glow" className="mb-6">Free Assessments</Badge>
            
            <h1 className="text-display-md md:text-display-lg lg:text-display-xl font-heading font-bold text-white mb-6">
              Complimentary Technology Assessments
            </h1>

            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-3xl mx-auto">
              UC Advisors provides free, no-obligation assessments to help you understand your current technology posture and identify opportunities for improvement. Our provider-agnostic approach ensures recommendations based solely on what's best for your organization.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" variant="glow" className="group">
                  Request an Assessment
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <a href={`tel:${companyInfo.phoneRaw}`}>
                <Button size="lg" variant="secondary">
                  <Phone className="w-5 h-5" />
                  {companyInfo.phone}
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Free Section */}
      <section className="section-padding bg-primary-600/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-4">
              Why Are Our Assessments Free?
            </h2>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto">
              As a provider-agnostic technology broker, we don't sell products—we provide strategic guidance. Our assessments help us understand your needs so we can recommend the right solutions, regardless of vendor.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: 'No Hidden Agenda',
                description: 'We\'re not tied to any single vendor. Our recommendations are based purely on what\'s best for your business.',
              },
              {
                title: 'Expert Analysis',
                description: '30+ years of combined experience in financial services technology. We know what works.',
              },
              {
                title: 'Actionable Insights',
                description: 'Every assessment includes a clear roadmap with prioritized recommendations you can act on.',
              },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-card p-6"
              >
                <div className="w-12 h-12 rounded-xl bg-accent-500/20 flex items-center justify-center mb-4">
                  <CheckCircle className="w-6 h-6 text-accent-400" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-slate-400">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Assessments Grid */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <Badge className="mb-4">Our Assessments</Badge>
            <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-4">
              Choose the Assessment That Fits Your Needs
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Each assessment is tailored to your organization and delivered with actionable recommendations.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {assessments.map((assessment, index) => (
              <motion.div
                key={assessment.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link to={assessment.href}>
                  <AnimatedCard className="h-full group">
                    <CardHeader>
                      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${assessment.bgGradient} flex items-center justify-center mb-4`}>
                        <assessment.icon className={`w-7 h-7 ${assessment.colorClass}`} />
                      </div>
                      <CardTitle className="group-hover:text-accent-400 transition-colors">
                        {assessment.shortTitle}
                      </CardTitle>
                      <CardDescription className="text-sm text-slate-500 mb-2">
                        {assessment.tagline}
                      </CardDescription>
                      <CardDescription className="text-base">
                        {assessment.description}
                      </CardDescription>
                      <div className="mt-4 flex items-center text-accent-400 text-sm font-medium group-hover:gap-2 transition-all">
                        Learn more
                        <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardHeader>
                  </AnimatedCard>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Overview */}
      <section className="section-padding bg-primary-600/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <Badge className="mb-4">Our Process</Badge>
            <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-4">
              How It Works
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Request', description: 'Fill out our simple request form or give us a call to get started.' },
              { step: '02', title: 'Discovery', description: 'We schedule a discovery call to understand your organization and goals.' },
              { step: '03', title: 'Assessment', description: 'Our experts conduct a thorough analysis of your current environment.' },
              { step: '04', title: 'Recommendations', description: 'You receive a detailed report with prioritized, actionable recommendations.' },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-5xl font-heading font-bold text-accent-400/20 mb-4">
                  {item.step}
                </div>
                <h3 className="text-lg font-heading font-semibold text-white mb-2">
                  {item.title}
                </h3>
                <p className="text-slate-400 text-sm">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  )
}
